const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SLUG = /^[a-z0-9-]{1,80}$/;
const STEPS = new Set(['selection', 'extras', 'details', 'confirmation']);
const ERRORS = new Set([
  'BOOKING_MAPPING_INVALID', 'VALIDATION', 'LOAD_FAILED', 'HOLD_EXPIRED',
  'BOOKING_CONTINUATION_EXPIRED', 'BOOKING_CONTINUATION_TOO_LARGE', 'CONTENT_TOO_TALL',
]);

export function bookingOrigin(provider, parent) {
  try {
    const url = new URL(provider);
    const host = new URL(parent);
    if (url.username || url.password || url.search || url.hash || url.pathname !== '/') return null;
    const local = ['localhost', '127.0.0.1'].includes(host.hostname);
    if (local && ['localhost', '127.0.0.1'].includes(url.hostname) && url.protocol === 'http:') return url.origin;
    if (host.protocol !== 'https:' || url.protocol !== 'https:') return null;
    const providers = {
      'https://shop.vspots.com': 'https://booking.vspots.com',
      'https://shop-staging.vspots.com': 'https://staging-booking.vspots.com',
      'https://vspots-development-staging.myshopify.com': 'https://staging-booking.vspots.com',
    };
    return providers[host.origin] === url.origin ? url.origin : null;
  } catch {
    return null;
  }
}

export function bookingMapping(config, variantId) {
  if (config?.enabled !== true) return null;
  const variant = config.variants?.[String(variantId)];
  if (!variant || variant.invalid) return null;
  const location = variant.location ?? config.location;
  if (!location || !UUID.test(location.id ?? '') || !SLUG.test(location.slug ?? '')) return null;
  if (!['services', 'restaurant', 'events'].includes(location.kind)) return null;
  const changedLocation = variant.location && variant.location.id !== config.location?.id;
  const service = variant.service || (!changedLocation && config.service) || '';
  const family = (!changedLocation && config.family) || '';
  if ((service && !UUID.test(service)) || (family && !UUID.test(family))) return null;
  if (location.kind === 'services' && !service && !family) return null;
  if (location.kind !== 'services' && (service || family)) return null;
  return {
    venue_id: location.id,
    venue_slug: location.slug,
    service_id: service,
    service_family_id: family,
    lang: config.lang === 'en' ? 'en' : 'de',
    name: String(location.name || ''),
  };
}

export function bookingURL(origin, mapping, parent, instance, standalone = false) {
  const url = new URL(`/${standalone ? 'book' : 'widget'}/${mapping.venue_slug}`, origin);
  for (const key of ['venue_id', 'service_id', 'service_family_id', 'lang']) {
    if (mapping[key]) url.searchParams.set(key, mapping[key]);
  }
  if (!standalone) {
    url.searchParams.set('parent_origin', parent);
    url.searchParams.set('instance', instance);
  }
  return url.href;
}

export function bookingMessage(event, origin, source, instance) {
  const data = event.data;
  if (event.origin !== origin || event.source !== source || !data || data.version !== 1 || data.instance !== instance) return null;
  if (data.type === 'vspots.booking.height' && Number.isInteger(data.height) && data.height >= 360 && data.height <= 30000) {
    return { type: 'height', height: data.height };
  }
  if (data.type === 'vspots.booking.ready') return { type: 'ready' };
  if (data.type === 'vspots.booking.close-request') return { type: 'close' };
  if (data.type === 'vspots.booking.step' && STEPS.has(data.step)) return { type: 'step', step: data.step };
  if (data.type === 'vspots.booking.error' && ERRORS.has(data.code)) return { type: 'error', code: data.code };
  return null;
}

if (typeof document !== 'undefined' && !window.vspotsBookingInstalled) {
  window.vspotsBookingInstalled = true;
  let current;
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    node.className = className;
    if (text) node.textContent = text;
    return node;
  };
  const read = (root) => {
    try {
      const config = JSON.parse(root.querySelector('[data-booking-config]').textContent);
      const id = root.dataset.selectedVariant || config.selectedVariant;
      return { config, mapping: bookingMapping(config, id), origin: bookingOrigin(config.origin, location.origin) };
    } catch {
      return { config: {}, mapping: null, origin: null };
    }
  };
  const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
  const refresh = (root) => {
    const { config, mapping, origin } = read(root);
    const button = root.querySelector('[data-booking-open]');
    const note = root.querySelector('[data-booking-unavailable]');
    if (button) button.hidden = !mapping || !origin;
    if (note) {
      note.hidden = Boolean(mapping && origin);
      note.textContent = config.text?.unavailable || '';
    }
  };
  const roots = () => document.querySelectorAll('[data-vspots-booking]');
  roots().forEach(refresh);
  document.addEventListener('shopify:section:load', () => roots().forEach(refresh));
  document.addEventListener('shopify:section:unload', () => {
    if (current?.dialog.open) current.dialog.close();
  });
  document.addEventListener('shopify:product:select', async (event) => {
    const root = event.target.closest?.('.shopify-section');
    const productId = event.target.closest?.('[data-product-id]')?.dataset.productId;
    const targets = productId
      ? [...roots()].filter((target) => target.dataset.bookingProductId === String(productId))
      : root?.querySelectorAll('[data-vspots-booking]');
    if (!targets?.length) return;
    const marker = {};
    for (const target of targets) {
      target.bookingPending = marker;
      target.dataset.selectedVariant = '';
      target.querySelector('[data-booking-open]').hidden = true;
    }
    try {
      const result = await event.promise;
      const id = result?.detail?.resource?.id ?? result?.variant?.id;
      for (const target of targets) {
        if (target.bookingPending !== marker) continue;
        target.dataset.selectedVariant = id ? String(id).split('/').pop() : 'unmapped';
        refresh(target);
      }
    } catch {
      for (const target of targets) {
        if (target.bookingPending !== marker) continue;
        target.dataset.selectedVariant = 'unmapped';
        refresh(target);
      }
    }
  });
  const create = (config, mapping, origin) => {
    const text = config.text;
    const state = { instance: crypto.randomUUID(), mapping, origin, ready: false, loaded: false, mismatch: false };
    const dialog = element('dialog', 'vspots-booking-dialog');
    dialog.setAttribute('aria-label', text.title);
    const header = element('header', 'vspots-booking-dialog__header');
    const heading = element('div', 'vspots-booking-dialog__heading');
    if (config.logo) {
      const logo = element('img', 'vspots-booking-dialog__logo');
      logo.src = config.logo;
      logo.alt = 'vspots';
      logo.width = 110;
      logo.height = 36;
      heading.append(logo);
    }
    heading.append(element('strong', '', text.title), element('span', '', mapping.name));
    const close = element('button', 'vspots-booking-dialog__close', text.close);
    close.type = 'button';
    header.append(heading, close);
    const body = element('div', 'vspots-booking-dialog__body');
    const notice = element('div', 'vspots-booking-dialog__notice');
    notice.setAttribute('role', 'status');
    const status = element('div', 'vspots-booking-dialog__status', text.loading);
    status.setAttribute('role', 'status');
    const frame = element('iframe', 'vspots-booking-dialog__frame');
    frame.title = `${text.title} – ${mapping.name}`;
    frame.setAttribute('scrolling', 'no');
    frame.referrerPolicy = 'strict-origin';
    body.append(notice, status, frame);
    dialog.append(header, body);
    document.body.append(dialog);
    Object.assign(state, { dialog, frame, body, status, notice });
    const measure = () => {
      if (state.loaded) frame.contentWindow?.postMessage({ type: 'vspots.booking.measure', version: 1, instance: state.instance }, origin);
    };
    const directLink = (target, destination) => {
      target.append(element('p', '', text.newWarning));
      const link = element('a', 'vspots-booking-dialog__link', text.newPage);
      link.href = bookingURL(origin, destination, location.origin, state.instance, true);
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.addEventListener('click', (event) => {
        if (!window.confirm(text.newConfirm)) event.preventDefault();
      });
      target.append(link);
    };
    const failure = (code) => {
      if (state.invalid) return;
      clearTimeout(state.timer);
      status.replaceChildren();
      const invalid = ['BOOKING_MAPPING_INVALID', 'VALIDATION'].includes(code);
      state.invalid = invalid;
      if (invalid) frame.hidden = true;
      state.connectionError = code === 'LOAD_FAILED';
      status.append(element('p', '', invalid ? text.invalid : code === 'HOLD_EXPIRED' ? text.expired : text.error));
      if (!invalid) {
        const retry = element('button', 'vspots-booking-dialog__retry', text.retry);
        retry.type = 'button';
        retry.addEventListener('click', () => {
          measure();
          clearTimeout(state.timer);
          state.timer = setTimeout(() => failure('LOAD_FAILED'), 15000);
        });
        status.append(retry);
        if (state.ready) status.append(element('p', '', text.continueInside));
        else directLink(status, mapping);
      }
      status.append(element('p', '', text.contact));
    };
    const viewport = () => {
      if (!dialog.open) return;
      // Keep the popup inset when the on-screen keyboard shrinks the visual viewport.
      dialog.style.setProperty('--booking-viewport-height', `${window.visualViewport?.height ?? window.innerHeight}px`);
      dialog.style.setProperty('--booking-viewport-top', `${window.visualViewport?.offsetTop ?? 0}px`);
      measure();
    };
    const closeDialog = () => dialog.close();
    close.addEventListener('click', closeDialog);
    dialog.addEventListener('cancel', (event) => { event.preventDefault(); closeDialog(); });
    dialog.addEventListener('close', () => {
      clearTimeout(state.timer);
      clearInterval(state.heartbeat);
      Object.assign(document.body.style, state.bodyStyle);
      document.documentElement.style.overflow = state.overflow;
      window.scrollTo(0, state.scrollY);
      if (state.opener?.isConnected) state.opener.focus({ preventScroll: true });
    });
    frame.addEventListener('load', () => { state.loaded = true; measure(); });
    window.addEventListener('message', (event) => {
      const msg = bookingMessage(event, origin, frame.contentWindow, state.instance);
      if (!msg) return;
      if (msg.type === 'height') {
        state.lastHeight = Date.now();
        frame.style.height = `${msg.height}px`;
        if (state.ready && state.connectionError && !state.invalid) {
          state.connectionError = false;
          clearTimeout(state.timer);
          status.replaceChildren();
        }
      } else if (msg.type === 'ready') {
        if (state.invalid) return;
        state.ready = true;
        clearTimeout(state.timer);
        status.replaceChildren();
      } else if (msg.type === 'step') {
        if (state.step !== msg.step) body.scrollTo({ top: 0 });
        state.step = msg.step;
      } else if (msg.type === 'close') closeDialog();
      else if (msg.type === 'error') failure(msg.code);
    });
    window.visualViewport?.addEventListener('resize', viewport);
    window.addEventListener('resize', viewport);
    state.show = (opener, next) => {
      if (dialog.open) return;
      state.opener = opener;
      state.mismatch = !same(mapping, next);
      notice.replaceChildren();
      if (state.mismatch) {
        notice.append(element('p', '', text.changed));
        directLink(notice, next);
      }
      state.scrollY = window.scrollY;
      state.overflow = document.documentElement.style.overflow;
      state.bodyStyle = { position: document.body.style.position, top: document.body.style.top, width: document.body.style.width };
      Object.assign(document.body.style, { position: 'fixed', top: `${-state.scrollY}px`, width: '100%' });
      document.documentElement.style.overflow = 'hidden';
      dialog.showModal();
      viewport();
      close.focus({ preventScroll: true });
      measure();
      state.lastHeight = Date.now();
      clearTimeout(state.timer);
      clearInterval(state.heartbeat);
      if (!state.ready && !state.invalid) state.timer = setTimeout(() => failure('LOAD_FAILED'), 15000);
      state.heartbeat = setInterval(() => {
        if (!state.invalid && Date.now() - state.lastHeight > 30000) failure('LOAD_FAILED');
        measure();
      }, 10000);
    };
    frame.src = bookingURL(origin, mapping, location.origin, state.instance);
    return state;
  };
  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-booking-open]');
    if (!button) return;
    const root = button.closest('[data-vspots-booking]');
    const { config, mapping, origin } = read(root);
    if (!mapping || !origin) { refresh(root); return; }
    event.preventDefault();
    if (!current) current = create(config, mapping, origin);
    if (current.origin !== origin) return;
    current.show(button, mapping);
  });
}
